import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { VideoCamera, Upload, Trash, CircleNotch, Star, Check, X, PencilSimple, FilmReel } from "@phosphor-icons/react";
import { API, BACKEND_URL } from "@/lib/api";

/**
 * Base Video Library — user-supplied or auto-generated short talking-head
 * clips used as input to Magic Hour Lip Sync. Shown in Account Settings.
 */
const BaseVideoLibrary = ({ compact = false }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const fetchVideos = async () => {
    try {
      const { data } = await axios.get(`${API}/base-videos`);
      setVideos(data.videos || []);
    } catch (e) {
      setError("Failed to load base videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVideos(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError("Video too large — max 50MB. Trim your clip first.");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name.replace(/\.[^.]+$/, "").slice(0, 60));
      fd.append("is_default", videos.length === 0 ? "true" : "false");
      await axios.post(`${API}/base-videos/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchVideos();
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const setDefault = async (id) => {
    try {
      await axios.patch(`${API}/base-videos/${id}`, { is_default: true });
      await fetchVideos();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to set default");
    }
  };

  const saveName = async (id) => {
    try {
      await axios.patch(`${API}/base-videos/${id}`, { name: editName });
      setEditingId(null);
      await fetchVideos();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to rename");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this base video?")) return;
    try {
      await axios.delete(`${API}/base-videos/${id}`);
      await fetchVideos();
    } catch (e) {
      setError(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="base-video-library">
      {!compact && (
        <div>
          <h3 className="text-lg font-semibold text-zinc-900 mb-1 flex items-center gap-2">
            <FilmReel size={22} weight="duotone" className="text-[#E91E8C]" />
            Base Video Library
          </h3>
          <p className="text-sm text-zinc-500 mb-1">
            Short (15–30s) talking-head clips of yourself used for <strong>Magic Hour Lip Sync</strong> — 2–3× faster than Talking Photo, with way more natural motion.
          </p>
          <p className="text-xs text-zinc-400">
            Tip: shoot one silent "idle" clip looking at camera — you'll reuse it across every Auto Create.
          </p>
        </div>
      )}

      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700 flex items-start gap-2" data-testid="base-video-error">
          <X size={14} className="mt-0.5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-600"><X size={12} /></button>
        </div>
      )}

      {/* Upload card */}
      <div
        className="border-2 border-dashed border-zinc-200 rounded-xl p-5 text-center hover:border-[#E91E8C]/40 transition-all"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files[0]); }}
      >
        <Upload size={26} weight="duotone" className="text-zinc-400 mx-auto mb-2" />
        <p className="text-sm text-zinc-600 mb-3">
          {uploading ? "Uploading your clip..." : "Drag & drop a .mp4 / .mov clip or"}
        </p>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-4 py-2 bg-[#E91E8C] text-white rounded-lg text-sm font-semibold hover:bg-[#d4177d] disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="upload-base-video-btn"
        >
          {uploading ? <><CircleNotch size={14} className="animate-spin" /> Uploading...</> : <><Upload size={14} /> Choose File</>}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          onChange={(e) => handleUpload(e.target.files[0])}
          className="hidden"
          data-testid="base-video-file-input"
        />
        <p className="text-[11px] text-zinc-400 mt-2">Max 50MB · 15–30s ideal · portrait 9:16 recommended</p>
      </div>

      {/* Video list */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <CircleNotch size={20} className="animate-spin text-zinc-400" />
        </div>
      ) : videos.length === 0 ? (
        <div className="text-center py-6 text-sm text-zinc-400">
          No base videos yet. Upload one above, or just use Talking Photo (Auto Create works fine without this).
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {videos.map((v) => (
            <div
              key={v.id}
              data-testid={`base-video-${v.id}`}
              className={`rounded-xl border-2 overflow-hidden transition-all ${
                v.is_default ? "border-[#E91E8C] shadow-[0_0_0_3px_#E91E8C20]" : "border-zinc-200 hover:border-zinc-300"
              }`}
            >
              {v.status === "processing" ? (
                <div className="aspect-[9/16] bg-zinc-50 flex flex-col items-center justify-center">
                  <CircleNotch size={22} className="animate-spin text-[#E91E8C] mb-2" />
                  <p className="text-xs text-zinc-500">Generating idle clip...</p>
                </div>
              ) : v.status === "failed" ? (
                <div className="aspect-[9/16] bg-rose-50 flex flex-col items-center justify-center p-3">
                  <X size={22} className="text-rose-400 mb-2" />
                  <p className="text-[11px] text-rose-600 text-center">{v.error || "Generation failed"}</p>
                </div>
              ) : (
                <video
                  src={`${API}/base-videos/${v.id}/file`}
                  className="w-full aspect-[9/16] object-cover bg-zinc-900"
                  muted
                  playsInline
                  onMouseEnter={(e) => e.target.play().catch(() => {})}
                  onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                  data-testid={`base-video-preview-${v.id}`}
                />
              )}

              <div className="p-3 space-y-2">
                {editingId === v.id ? (
                  <div className="flex items-center gap-1">
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="input-field text-xs py-1 flex-1"
                      autoFocus
                    />
                    <button onClick={() => saveName(v.id)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"><Check size={12} weight="bold" /></button>
                    <button onClick={() => setEditingId(null)} className="p-1.5 text-zinc-400 hover:bg-zinc-100 rounded"><X size={12} /></button>
                  </div>
                ) : (
                  <button onClick={() => { setEditingId(v.id); setEditName(v.name); }} className="group flex items-center gap-1 text-sm font-semibold text-zinc-900 w-full text-left" data-testid={`rename-base-video-${v.id}`}>
                    <span className="flex-1 truncate">{v.name}</span>
                    <PencilSimple size={10} className="opacity-0 group-hover:opacity-100 text-zinc-400" />
                  </button>
                )}

                <div className="flex items-center justify-between">
                  {v.is_default ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E91E8C]/10 text-[#E91E8C] text-[10px] font-bold">
                      <Star size={10} weight="fill" /> DEFAULT
                    </span>
                  ) : v.status !== "processing" ? (
                    <button onClick={() => setDefault(v.id)} className="text-[10px] text-zinc-500 hover:text-[#E91E8C] font-semibold" data-testid={`set-default-${v.id}`}>
                      Set as default
                    </button>
                  ) : (
                    <span className="text-[10px] text-zinc-400">Processing...</span>
                  )}
                  <button onClick={() => remove(v.id)} className="p-1 text-zinc-400 hover:text-rose-500 rounded" data-testid={`delete-base-video-${v.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BaseVideoLibrary;
